// imports
import { AnyAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import React, { useState, useRef, useEffect } from 'react';

import {
  faSave, faXmarkCircle, faEdit, faTrashAlt
} from '@fortawesome/free-regular-svg-icons';
import { string, object, InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, SubmitHandler } from 'react-hook-form';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Popup from '../../../atoms/popup';
import { REGEX } from '../../../constants';
import Section from '../../../atoms/section';
import { IModal } from '../../../common/types';
import { updatePreferences, resetDelegateWallet } from '../../../repositories/store/thunks';
import { selectUserPreferences, selectUserDelegateWallet } from '../../../repositories/store/slices';

// interfaces / types / consts / schema
interface IBio { email: string; username: string; fullname: string }
const schema = object({
  fullname: string().label('Full Name').trim().min(10)
    .max(30)
    .required(),
  username: string().label('Username').trim().min(5)
    .max(20)
    .matches(REGEX.Username, 'Username should be alphanumeric only')
    .required(),
  email: string().label('Email').trim().matches(REGEX.Email, 'Email should be valid')
    .required(),
  delegateWallet: string().label('Delegate Wallet').required()
}).required();
type FormType = InferType<typeof schema>;

// exports
export default function Account(): JSX.Element {
  // hooks configuration
  const dispatch = useDispatch();
  const modalRef = useRef<IModal>();
  const [editMode, setEditMode] = useState(false);
  const submitRef = useRef<HTMLInputElement>(null);
  const { wallet, updateStatus: resetWalletStatus } = useSelector(selectUserDelegateWallet);
  const { updateStatus: updatePreferencesStatus, bio: { email, username, fullname } } = useSelector(selectUserPreferences);
  const delegateWallet = wallet?.address || '';
  const isResettingWallet = resetWalletStatus === 'loading';
  const isSavingPreferences = updatePreferencesStatus === 'loading';
  const {
    register, handleSubmit, setValue, clearErrors, formState: { errors }
  } = useForm<FormType>({
    resolver: yupResolver(schema)
  });
  // componentDidMount
  useEffect(() => {
    if (email || username || fullname || delegateWallet) {
      setValue('fullname', fullname);
      setValue('email', email);
      setValue('username', username);
      setValue('delegateWallet', delegateWallet);
    }
  }, [email, username, fullname, delegateWallet, setValue]);
  useEffect(() => {
    if (updatePreferencesStatus === 'success' && editMode) {
      setEditMode(false);
    }
  }, [updatePreferencesStatus, editMode]);

  // private methods
  const enableEdit = (): void => setEditMode(true);
  const reset = (): void => {
    setValue('fullname', fullname);
    setValue('username', username);
    setValue('email', email);
    clearErrors();
    setEditMode(false);
  };
  const onSubmit: SubmitHandler<FormType> = (data: IBio) => {
    setEditMode(true);
    setValue('fullname', data.fullname);
    setValue('username', data.username);
    setValue('email', data.email);
    dispatch(updatePreferences({ bio: data }) as unknown as AnyAction);
    return false;
  };
  const showEditMode = (): React.ReactElement => (editMode
    ? (
      <>
        <FontAwesomeIcon icon={faSave} onClick={() => submitRef.current!.click()} />
        <FontAwesomeIcon icon={faXmarkCircle} onClick={() => reset()} />
        <input type="submit" className="hidden" ref={submitRef} />
      </>
    )
    : <FontAwesomeIcon icon={faEdit} onClick={() => enableEdit()} />);

  // render
  return (
    <>
      <Popup
        status="info"
        ref={modalRef}
        btnText="Delete"
        title="Delegate Wallet"
        message="Are you sure you want to delete delegate wallet?"
        onClick={() => {
          dispatch(resetDelegateWallet() as unknown as AnyAction);
          modalRef.current?.hide();
        }}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Section heading="Basic Information">
          <div className="section-form">
            <div className="actions">
              {
                isSavingPreferences
                  ? <FontAwesomeIcon icon={faSpinner} spin />
                  : showEditMode()
              }
            </div>
            <div className="input-section">
              <div className="label">Full Name</div>
              <input
                type="text"
                autoComplete="off"
                placeholder="Enter Full name"
                disabled={!editMode || isSavingPreferences}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('fullname')}
              />
              {errors.fullname && <p className="error">{errors.fullname?.message}</p>}
            </div>
            <div className="input-section">
              <div className="label">User Name</div>
              <input
                type="text"
                autoComplete="off"
                placeholder="Enter user name"
                disabled={!editMode || isSavingPreferences}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('username')}
              />
              {errors.username && <p className="error">{errors.username?.message}</p>}
            </div>
          </div>
        </Section>
        <Section heading="Contact Information">
          <div className="section-form">
            <div className="input-section">
              <div className="label">Email Address</div>
              <input
                type="text"
                autoComplete="off"
                disabled={!editMode || isSavingPreferences}
                placeholder="Enter email address"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('email')}
              />
              {errors.email && <p className="error">{errors.email?.message}</p>}
            </div>
          </div>
        </Section>
        <Section heading="Delegate Wallet Information">
          <div className="section-form">
            <div className="actions">
              {
                isResettingWallet
                  ? <FontAwesomeIcon icon={faSpinner} spin />
                  : <FontAwesomeIcon icon={faTrashAlt} onClick={() => modalRef.current?.show()} />
              }
            </div>
            <div className="input-section">
              <div className="label">Public Address</div>
              <input
                disabled
                type="text"
                autoComplete="off"
                placeholder="Enter delegate wallet address"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('delegateWallet')}
              />
            </div>
          </div>
        </Section>
      </form>
    </>
  );
}
